import { LoadingButton } from "@mui/lab";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {
	Contact,
	useGetAllMembersOfLazyQuery,
	useWorksInDepartmentMutation,
} from "@repo/server-api";
import { OptionType } from "@repo/server-api/types";
import { useNotification } from '@repo/shared/components';
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import * as Yup from "yup";

import { validErrorState } from "@repo/legacy/components/helper/Index";

import Dropdown from "../sections/Dropdown";

export default function DialogEmployee(
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		refreshData: () => void;
		departmantId: string;
		contactId: string;
	}>,
) {
	const { t } = useTranslation();

	const { showNotification } = useNotification();

	const [open, setOpen] = useState(false);

	const [employees, setEmployees] = useState<Contact[]>([]);
	const [optionElement, setOptionElement] = useState<OptionType | null>(null);

	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const [errors, setErrors] = useState<Record<string, boolean | object | string>>({});

	const [worksInDepartment] = useWorksInDepartmentMutation({
		variables: {
			contactId: optionElement?.item?.id ?? "",
			departmentId: props.departmantId,
		},
		onCompleted(data) {
			if (data.worksInDepartment.worksInDepartmentInformation?.id) {
				props.refreshData();
				handleClose();
			} else if (data.worksInDepartment.errors) {
				const errors = data.worksInDepartment.errors;
				errors.forEach((error) => {
					if ("errors" in error && Array.isArray(error.errors)) {
						if ("message" in error && error.errors?.length === 0)
							showNotification(error.message, "error");
						else if ("errorMessage" in error)
							error.errors.forEach((e) => {
								showNotification(e.errorMessage, "error");
							});
					}
				});
				setSaveClicked(false);
			}
		},
		onError(error) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	const [getEmployees] = useGetAllMembersOfLazyQuery({
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data && data.allMembersOf) {
				setEmployees(data.allMembersOf.nodes as unknown as Contact[]);
			}
		},
	});

	useEffect(() => {
		setOpen(props.open);
		setOptionElement({
			title: "",
			inputValue: "",
			item: null,
		});
		setErrors({});
		getEmployees({
			variables: {
				contactId: props.contactId,
				first: 100,
				after: null,
			},
		});
	}, [props.open]);

	const validationSchema = Yup.object().shape({
		id: Yup.string().required(t("validation.member.required")),
	});

	const validateFields = async () => {
		const localErrors: Record<string, boolean | object | string> = {};
		try {
			await validationSchema.validate(optionElement?.item, {
				abortEarly: false,
			});
		} catch (err) {
			if (err instanceof Yup.ValidationError) {
				err.inner.forEach((error) => {
					if (error.path) {
						localErrors[error.path] = error.message;
					}
				});
			}
		}

		if (Object.keys(localErrors).length === 0) {
			if (!optionElement?.item?.id) {
				localErrors.id = t("validation.member.required");
			}
		}

		setErrors({ ...localErrors });

		return validErrorState(localErrors);
	};

	const handleClose = () => {
		setOpen(false);
		setErrors({});
		setOptionElement({
			title: "",
			inputValue: "",
			item: null,
		});
		props.setOpen(false);
		setSaveClicked(false);
	};

	const handleSubmit = async () => {
		const isValid = await validateFields();
		if (isValid) {
			setSaveClicked(true);
			worksInDepartment();
		}
	};

	return (
		<Dialog open={open} maxWidth="sm" fullWidth={true}>
			<DialogContent className="flex-col gap-6">
				<Box className="fullCenter">
					<Dropdown
						doNotShowMenu={true}
						element={{
							title: optionElement?.title ?? "",
							inputValue: optionElement?.inputValue ?? "",
							item: optionElement?.item ?? null,
						}}
						setElement={(o) => {
							setOptionElement(o);
							setErrors({ ...errors, id: "" });
						}}
						elements={employees}
						error={typeof errors?.id === "string" ? errors.id : ""}
						setError={(value) => setErrors?.({ ...errors, id: value })}
						searchPlaceholder={t("dropdown.placeholderEmployee")}
						searchLabel={t("dropdown.employeeSearch")}
						noData={t("dropdown.noEmployees")}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={handleClose} autoFocus>
					{t("cancel")}
				</Button>
				<LoadingButton
					loading={saveClicked}
					variant="contained"
					onClick={handleSubmit}
					className={saveClicked ? "" : "blueButton"}>
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
